import { useCallback, useEffect, useMemo, useRef, useState } from "react";
import { useDispatch, useSelector } from "react-redux";
import { useInfiniteQuery, useQueryClient } from "@tanstack/react-query";
import { clientQuery } from "Core/data/GraphQLApi";
import { endpoints } from "Core/defaultValues";
import { getInitialRange } from "Core/data/Helpers";
import { formatAvailableMedia } from "Modules/video/.globals/helpers";
import { savePreference } from "Redux/actions";
import { query as searchVideoEventsQuery, mergeEventsById, removeEventsByIds } from "./redux/slice";

export const EVENTS_LIST_QUERY_KEY = ["videoEvents", "search"];
export const DEFAULT_TIME_FRAME = "today";

const DEFAULT_FILTERS = {
  openSearch: "", dateRange: {}, events: [], hasNotes: null,
  starred: false, mediaDeleted: false, mediaType: "All",
  reviewStatus: null, entities: [], limit: 20, timeFrame: DEFAULT_TIME_FRAME,
};

const buildVariables = (filters, nextToken) => ({
  limit: filters.limit || 20,
  dateRange: filters.dateRange,
  openSearch: filters.openSearch,
  nextToken,
  starred: filters.starred,
  entities: filters.entities?.length ? filters.entities : [{ name: "All", id: -1 }],
  eventNames: filters.events,
  mediaDeleted: filters.mediaDeleted,
  mediaType: filters.mediaType,
  reviewStatus: filters.reviewStatus,
  hasNotes: filters.hasNotes,
});

/**
 * Listado + paginación (TanStack Query) del tab "Events" de Video, junto con el
 * puente de reconciliación con las escrituras clásicas de Redux (updateEventsData/
 * deleteEvent, compartidas con Starred/Deleted, fuera de alcance de esta migración)
 * y el manejo de filtros/búsqueda.
 */
export const useEventsSearch = ({ tabName, enabled, tableViewRef }) => {
  const dispatch = useDispatch();
  const queryClient = useQueryClient();

  const { authenticatedId } = useSelector((state) => state.securityRedux);
  const lastPatch = useSelector((state) => state.videoEventsListRedux.lastPatch);

  const [preferencesReady, setPreferencesReady] = useState(false);
  const [filters, setFiltersState] = useState(DEFAULT_FILTERS);
  const [selectedRows, setSelectedRows] = useState([]);
  const [searchText, setSearchText] = useState("");
  const [typing, setTyping] = useState(false);

  const queryKey = useMemo(() => [...EVENTS_LIST_QUERY_KEY, filters], [filters]);

  const infiniteQuery = useInfiniteQuery({
    queryKey,
    enabled: enabled && preferencesReady,
    initialPageParam: null,
    queryFn: ({ pageParam }) =>
      clientQuery(searchVideoEventsQuery, buildVariables(filters, pageParam), endpoints.GRAPHQL_GENERAL_V2)
        .then((res) => ({
          items: formatAvailableMedia(res?.data?.items || []),
          nextToken: res?.data?.nextToken || null,
        })),
    getNextPageParam: (lastPage) => lastPage.nextToken,
  });

  const data = useMemo(() => {
    const seen = new Set();
    const merged = [];
    (infiniteQuery.data?.pages || []).forEach((page) => {
      page.items.forEach((item) => {
        if (item?.id != null) {
          if (seen.has(item.id)) return;
          seen.add(item.id);
        }
        merged.push(item);
      });
    });
    return merged;
  }, [infiniteQuery.data]);

  const filtersRef = useRef(filters);
  useEffect(() => {
    filtersRef.current = filters;
  }, [filters]);

  const appliedPatchSeqRef = useRef(0);
  useEffect(() => {
    if (!enabled) return;
    if (!lastPatch || lastPatch.seq === appliedPatchSeqRef.current) return;
    appliedPatchSeqRef.current = lastPatch.seq;

    queryClient.setQueriesData({ queryKey: EVENTS_LIST_QUERY_KEY }, (old) => {
      if (!old) return old;
      return {
        ...old,
        pages: old.pages.map((page) => ({
          ...page,
          items:
            lastPatch.type === "update"
              ? mergeEventsById(page.items, lastPatch.events)
              : removeEventsByIds(page.items, lastPatch.eventIds),
        })),
      };
    });
  }, [lastPatch, enabled, queryClient]);

  const handleSearch = useCallback((newSearchText) => {
    const newFilters = { ...filters, openSearch: newSearchText };
    const filtersToPersist = { ...newFilters };
    delete filtersToPersist.openSearch;
    tableViewRef.current?.setSelectedRows([]);
    dispatch(savePreference({
      entityName: "User", entityId: authenticatedId,
      preferenceKey: tabName, preferenceValue: { filters: filtersToPersist },
    }));
    tableViewRef?.current?.setNewPage(1);
    setFiltersState(newFilters);
  }, [filters, authenticatedId, tabName]);

  useEffect(() => {
    if (typing) {
      const timer = setTimeout(() => handleSearch(searchText), 1000);
      return () => clearTimeout(timer);
    }
  }, [searchText]);

  const handleChangePage = useCallback(() => {
    if (infiniteQuery.hasNextPage && !infiniteQuery.isFetchingNextPage) {
      infiniteQuery.fetchNextPage();
    }
  }, [infiniteQuery.hasNextPage, infiniteQuery.isFetchingNextPage, infiniteQuery.fetchNextPage]);

  const handleFiltersChange = useCallback((newFilters) => {
    const filtersToPersist = { ...newFilters };
    delete filtersToPersist.openSearch;
    dispatch(savePreference({
      entityName: "User", entityId: authenticatedId,
      preferenceKey: tabName, preferenceValue: { filters: filtersToPersist },
    }));
    tableViewRef?.current?.setNewPage(1);
    setFiltersState(newFilters);
  }, [authenticatedId, tabName]);

  const handleInitialPreferences = useCallback((userPreferences) => {
    let newFilters;
    if (!userPreferences?.filters) {
      newFilters = {
        ...DEFAULT_FILTERS,
        dateRange: getInitialRange(DEFAULT_TIME_FRAME),
      };
      const filtersToPersist = { ...newFilters };
      delete filtersToPersist.openSearch;
      dispatch(savePreference({
        entityName: "User", entityId: authenticatedId,
        preferenceKey: tabName, preferenceValue: { ...userPreferences, filters: filtersToPersist },
      }));
    } else {
      newFilters = {
        ...userPreferences.filters,
        openSearch: filtersRef.current.openSearch,
        dateRange: getInitialRange(
          userPreferences.filters?.timeFrame || DEFAULT_TIME_FRAME,
          userPreferences.filters?.dateRange?.start,
          userPreferences.filters?.dateRange?.end
        ),
      };
    }
    setFiltersState(newFilters);
    setPreferencesReady(true);
  }, [authenticatedId, tabName]);

  return {
    data,
    filters,
    loading: infiniteQuery.isLoading,
    isFetching: infiniteQuery.isFetching,
    searchText,
    onSearchTextChange: useCallback((value) => { setSearchText(value); setTyping(true); }, []),
    selectedRows, setSelectedRows,
    handleSearch, handleChangePage, handleFiltersChange, handleInitialPreferences,
    invalidate: useCallback(() => queryClient.invalidateQueries({ queryKey: EVENTS_LIST_QUERY_KEY }), [queryClient]),
  };
};
